/**
 * Tiny CSV helper used by the front-end exports. The backend will gain a
 * proper /v1/exports endpoint in a later sprint; meanwhile this lets us
 * ship a useful "Exporter" button that works against whatever the
 * caller has already fetched into memory.
 *
 * The helper deliberately doesn't handle huge datasets — at >10 k rows
 * the operator should ask the platform to produce the file instead. The
 * UI guards against that case via a hard cap.
 */

const MAX_EXPORT_ROWS = 10_000;

export type CsvColumn<T> = {
  header: string;
  /** Function that returns the printable value for the row. */
  value: (row: T) => string | number | null | undefined;
};

/**
 * Builds a CSV string from a list of rows + column descriptors. Quotes
 * field values that contain commas, quotes, or newlines (RFC 4180).
 */
export function toCSV<T>(rows: T[], columns: CsvColumn<T>[]): { csv: string; truncated: boolean } {
  const truncated = rows.length > MAX_EXPORT_ROWS;
  const usable = truncated ? rows.slice(0, MAX_EXPORT_ROWS) : rows;
  const header = columns.map((c) => quote(c.header)).join(',');
  const body = usable
    .map((row) => columns.map((c) => quote(toString(c.value(row)))).join(','))
    .join('\n');
  return { csv: header + '\n' + body + '\n', truncated };
}

function toString(v: string | number | null | undefined): string {
  if (v == null) return '';
  return typeof v === 'number' ? v.toString() : v;
}

function quote(v: string): string {
  if (v == null) return '';
  if (/[",\n\r]/.test(v)) {
    return `"${v.replace(/"/g, '""')}"`;
  }
  return v;
}

/**
 * Triggers a browser download for the supplied CSV string. Adds a BOM
 * so Excel detects UTF-8 without prompting for an encoding.
 */
export function downloadCSV(filename: string, csv: string): void {
  const blob = new Blob(['﻿' + csv], { type: 'text/csv;charset=utf-8;' });
  const url = URL.createObjectURL(blob);
  const a = document.createElement('a');
  a.href = url;
  a.download = filename;
  document.body.appendChild(a);
  a.click();
  setTimeout(() => {
    document.body.removeChild(a);
    URL.revokeObjectURL(url);
  }, 0);
}
